import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarRange, FileSpreadsheet, Loader2, Plus, Trash2, LayoutTemplate, Sparkles } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { CopilotDock } from "../../components/os/CopilotDock";
import ImportWizard from "./planning/ImportWizard";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-violet transition-colors";

const ESTADO_META = {
  borrador: ["Borrador", "bg-navy/5 text-navy/60 border-navy/15"],
  configurando: ["Configurando", "bg-blue-50 text-blue-700 border-blue-200"],
  listo: ["Listo para planificar", "bg-blue-50 text-blue-700 border-blue-200"],
  planificado: ["Planificado", "bg-violet/10 text-violet border-violet/30"],
  aprobado: ["Aprobado", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  en_ejecucion: ["En ejecución", "bg-amber-50 text-amber-700 border-amber-200"],
  en_riesgo: ["En riesgo", "bg-red-50 text-red-700 border-red-200"],
  completado: ["Completado", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  cerrado: ["Cerrado", "bg-navy/5 text-navy/60 border-navy/15"],
};

export const EstadoBadge = ({ estado }) => {
  const [label, cls] = ESTADO_META[estado] || [estado, "bg-navy/5 text-navy/60 border-navy/15"];
  return <span className={`text-[11px] px-2.5 py-1 rounded-full border ${cls}`} data-testid="plan-estado">{label}</span>;
};

const EMPTY = { nombre: "", descripcion: "", pais: "Argentina", region: "Buenos Aires", fecha_inicio: "", fecha_cierre: "", responsable: "", template: "infraestructura" };

export default function Planning() {
  const [plans, setPlans] = useState(null);
  const [modal, setModal] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/planning/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/planning/plans", modal);
      toast.success("Plan creado");
      navigate(`/os/planning/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error al crear"); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar el plan "${p.nombre}"? (irá a la Papelera)`)) return;
    try { await api.delete(`/planning/plans/${p.id}`); toast.success("Plan eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  return (
    <OSLayout title="OMNIA Planning">
      <div className="space-y-6 p425" data-testid="planning-page">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <p className="text-sm text-navy/50 max-w-2xl">Describe tu operación (unidades, recursos, productividades y reglas en lenguaje natural) y OMNIA genera un plan ejecutable, auditable y dinámico.</p>
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => setImportOpen(true)} data-testid="import-excel-btn"
              className="rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet inline-flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" /> Importar desde Excel
            </button>
            <button onClick={() => setModal({ ...EMPTY })} data-testid="new-plan-btn"
              className="rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Nuevo plan
            </button>
          </div>
        </div>

        {plans === null ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy/30" /></div>
        ) : plans.length === 0 ? (
          <div className="rounded-xl border border-dashed border-navy/15 bg-white p-14 text-center" data-testid="planning-empty">
            <CalendarRange className="w-10 h-10 mx-auto text-navy/20 mb-3" />
            <p className="font-medium">Aún no tienes planes</p>
            <p className="text-sm text-navy/40 mt-1">Crea tu primer plan: puedes empezar desde cero o usar la plantilla de mantenimiento de infraestructura.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((p) => (
              <Link key={p.id} to={`/os/planning/${p.id}`} data-testid={`plan-card-${p.id}`}
                className="rounded-xl border border-navy/10 bg-white p-5 hover:border-violet transition-colors group relative">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium group-hover:text-violet transition-colors">{p.nombre}</div>
                  <EstadoBadge estado={p.estado} />
                </div>
                <div className="text-xs text-navy/40 mt-1">{p.pais}{p.region ? ` · ${p.region}` : ""}</div>
                <div className="text-xs text-navy/50 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  <span>{p.work_units_count} {p.work_unit_name || "unidades"}{p.work_units_count === 1 ? "" : "s"}</span>
                  <span>{(p.activities || []).length} actividades</span>
                  <span>v{p.version || 0}</span>
                </div>
                <div className="text-[11px] text-navy/35 mt-2">{p.fecha_inicio || "—"} → {p.fecha_cierre || "—"}</div>
                <button onClick={(e) => { e.preventDefault(); del(p); }} title="Eliminar" data-testid={`plan-delete-${p.id}`}
                  className="absolute bottom-3 right-3 text-navy/20 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-4 h-4" /></button>
              </Link>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-cream rounded-lg w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="new-plan-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2"><CalendarRange className="w-4 h-4 text-violet" /> Nuevo plan</h3>
              <button type="button" onClick={() => setModal(null)} className="text-navy/40 hover:text-navy">✕</button>
            </div>
            <input className={inputCls} placeholder="Nombre del plan" value={modal.nombre} required data-testid="plan-nombre"
              onChange={(e) => setModal({ ...modal, nombre: e.target.value })} />
            <textarea className={inputCls} rows={2} placeholder="Descripción (opcional)" value={modal.descripcion}
              onChange={(e) => setModal({ ...modal, descripcion: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input className={inputCls} placeholder="País" value={modal.pais} data-testid="plan-pais"
                onChange={(e) => setModal({ ...modal, pais: e.target.value })} />
              <input className={inputCls} placeholder="Región / Provincia / Estado" value={modal.region}
                onChange={(e) => setModal({ ...modal, region: e.target.value })} />
              <label className="text-[10px] text-navy/40">Fecha de inicio
                <input className={inputCls} type="date" value={modal.fecha_inicio} required data-testid="plan-inicio"
                  onChange={(e) => setModal({ ...modal, fecha_inicio: e.target.value })} /></label>
              <label className="text-[10px] text-navy/40">Fecha objetivo de cierre
                <input className={inputCls} type="date" value={modal.fecha_cierre} required data-testid="plan-cierre"
                  onChange={(e) => setModal({ ...modal, fecha_cierre: e.target.value })} /></label>
            </div>
            <input className={inputCls} placeholder="Responsable" value={modal.responsable}
              onChange={(e) => setModal({ ...modal, responsable: e.target.value })} />
            <div className="space-y-2">
              <div className="overline text-navy/40">Plantilla</div>
              {[["infraestructura", "Mantenimiento preventivo de infraestructura", "Nodos, zonas, postes, cuadrillas y productividades preconfiguradas", LayoutTemplate],
                ["", "Empezar desde cero", "Define tu propia unidad de trabajo, variables, actividades y recursos", Sparkles]].map(([val, t, d, Icon]) => (
                <button key={val} type="button" onClick={() => setModal({ ...modal, template: val })} data-testid={`tpl-${val || "scratch"}`}
                  className={`w-full text-left rounded-lg border p-3 flex gap-3 transition-colors ${modal.template === val ? "border-violet bg-violet/5" : "border-navy/10 bg-white hover:border-navy/25"}`}>
                  <Icon className="w-4 h-4 mt-0.5 text-violet shrink-0" />
                  <span><span className="text-sm font-medium block">{t}</span><span className="text-xs text-navy/40">{d}</span></span>
                </button>
              ))}
            </div>
            <button disabled={busy} data-testid="plan-submit"
              className="w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear plan
            </button>
          </form>
        </div>
      )}
      {importOpen && <ImportWizard onClose={() => setImportOpen(false)} />}
      <CopilotDock title="Copiloto · Planning"
        suggestions={["¿Cómo van mis planes?", "¿Qué plan está en riesgo y por qué?", "¿Qué unidades comprometen el cierre del plan más reciente?"]} />
    </OSLayout>
  );
}
